/* eslint-disable no-use-before-define */
import { getRequest } from '../core/apiRequests';
import { clientbookApi } from '../core/clientbook-api';
import { CB_INTERNAL_API_URL } from '../constants';
import { apiCache, invalidateClientCache } from './apiCache';

export const getClients = async ({ data = {}, loadingStatusOff = false, axiosConfig = {} }) => {
  return clientbookApi.post({
    url: '/searches/clients',
    data,
    loading: !loadingStatusOff,
    axiosConfig,
  });
};

export const createClient = async ({ data, loadingStatusOff = false, axiosConfig = {} }) => {
  return clientbookApi.post({
    url: '/clients',
    data,
    loading: !loadingStatusOff,
    axiosConfig,
  });
};

export const deleteClient = async ({ clientId, loadingStatusOff = false, axiosConfig = {} }) => {
  // Invalidate cache before delete
  invalidateClientCache(clientId);

  return clientbookApi.delete({
    url: `/clients/${clientId}`,
    loading: !loadingStatusOff,
    axiosConfig,
  });
};

export const updateClient = async ({ data, clientId, loadingStatusOff = false, axiosConfig = {} }) => {
  // Invalidate cache before update
  invalidateClientCache(clientId);

  return clientbookApi.patch({
    url: `/clients/${clientId}`,
    data: parseData(data),
    loading: !loadingStatusOff,
    axiosConfig,
  });
};

export const mergeClient = async ({ data, loadingStatusOff = false, axiosConfig = {}, query = {} }) => {
  return clientbookApi.put({
    url: `/clients/${data.id}`,
    data,
    loading: !loadingStatusOff,
    axiosConfig,
  });
};

export const getMergePreview = async ({ clientId, sourceClientId, loadingStatusOff = false }) => {
  return clientbookApi.get({
    url: `/clients/${clientId}/merge-preview/${sourceClientId}`,
    loading: !loadingStatusOff,
  });
};

const CB_API_BASE = CB_INTERNAL_API_URL || 'https://clientbook-api-dev.clbk.app';

export const mergeClientsV2 = async ({ clientId, sourceClientId, data = {}, loadingStatusOff = false }) => {
  return clientbookApi.post({
    url: `${CB_API_BASE}/api/v2/clients/${clientId}/merge/${sourceClientId}`,
    data,
    loading: !loadingStatusOff,
  });
};

export const mergeTwoClients = async ({ data, loadingStatusOff = false, axiosConfig = {} }) => {
  return clientbookApi.post({
    url: `/clients/${data.primaryClientId}/merge/${data.secondaryClientId}`,
    data: {
      id: data.secondaryClientId,
      storeId: data.storeId,
    },
    loading: !loadingStatusOff,
    axiosConfig,
  });
};

export const getClientActivities = async ({ clientId, query = {}, loadingStatusOff = true, axiosConfig = {} }) => {
  return clientbookApi.get({
    url: `/clients/${clientId}/activities`,
    query,
    loadingStatusOff,
    axiosConfig,
  });
};

// Validation helpers
const validateClientId = clientId => {
  if (clientId === null || clientId === undefined || clientId === '') {
    throw new Error('Invalid clientId: must be provided');
  }
};

const validateTagId = tagId => {
  const numericTagId = Number(tagId);
  if (!Number.isInteger(numericTagId) || numericTagId <= 0) {
    throw new Error(`Invalid tagId: must be a positive integer, got ${tagId}`);
  }
  return numericTagId;
};

const CLIENT_CACHE_TTL_MS = 30000; // 30 seconds

export const getClientById = async ({ clientId, loadingStatusOff = false, axiosConfig = {}, useCache = false }) => {
  validateClientId(clientId);

  // Check cache if caching is enabled
  if (useCache) {
    const cacheKey = apiCache.generateKey('client', clientId);
    const cachedValue = apiCache.get(cacheKey);
    if (cachedValue !== undefined) {
      return cachedValue;
    }
  }

  const result = await clientbookApi.get({
    url: `/clients/${clientId}`,
    loading: !loadingStatusOff,
    axiosConfig,
  });

  // Cache the result if caching is enabled
  if (useCache && result) {
    const cacheKey = apiCache.generateKey('client', clientId);
    apiCache.set(cacheKey, result, CLIENT_CACHE_TTL_MS);
  }

  return result;
};

const DEFAULT_TAG_LIMIT = 50;

export const getClientTags = async ({ clientId, query = {}, loadingStatusOff = false, axiosConfig = {} }) => {
  validateClientId(clientId);
  return clientbookApi.get({
    url: `/clients/${clientId}/tags`,
    query: { offset: query.offset || 0, limit: query.limit || DEFAULT_TAG_LIMIT },
    loading: !loadingStatusOff,
    axiosConfig,
  });
};

const DEFAULT_MERCHANT_TAG_PAGE_SIZE = 500;

export const getMerchantTags = async ({ query = {}, loadingStatusOff = false, axiosConfig = {} }) => {
  const queryParams = {
    offset: query.offset || 0,
    limit: query.limit || DEFAULT_MERCHANT_TAG_PAGE_SIZE,
  };
  // Only include archived param if explicitly set (API may not support it)
  if (query.archived !== undefined) {
    queryParams.archived = query.archived;
  }
  const response = await clientbookApi.get({
    url: '/merchants/tags',
    query: queryParams,
    loading: !loadingStatusOff,
    axiosConfig,
  });
  const parsedTotal = Number(response?.total);
  const total = response?.total == null || !Number.isFinite(parsedTotal) ? null : parsedTotal;
  // Return full response with total for callers that need pagination info
  return {
    results: response?.results || [],
    total,
  };
};

export const getAllMerchantTags = async ({ query = {}, loadingStatusOff = false, axiosConfig = {} }) => {
  const pageSize = query.limit || DEFAULT_MERCHANT_TAG_PAGE_SIZE;

  // First request to get total count
  const firstPage = await getMerchantTags({
    query: { ...query, offset: 0, limit: pageSize },
    loadingStatusOff,
    axiosConfig,
  });

  const { total } = firstPage;
  const allTags = [...firstPage.results];

  if (total !== null) {
    // If the API provides a total, fetch remaining pages in parallel.
    const remainingPages = Math.ceil(Math.max(total - allTags.length, 0) / pageSize);
    const requests = [];

    for (let i = 1; i <= remainingPages; i++) {
      requests.push(
        getMerchantTags({
          query: { ...query, offset: i * pageSize, limit: pageSize },
          loadingStatusOff,
          axiosConfig,
        }),
      );
    }

    const responses = await Promise.all(requests);
    responses.forEach(response => {
      allTags.push(...response.results);
    });

    return allTags;
  }

  // If the API omits total, keep paging until a partial page is returned.
  let lastPageSize = allTags.length;
  let nextOffset = pageSize;
  while (lastPageSize === pageSize) {
    const page = await getMerchantTags({
      query: { ...query, offset: nextOffset, limit: pageSize },
      loadingStatusOff,
      axiosConfig,
    });
    const pageResults = page.results || [];
    lastPageSize = pageResults.length;
    allTags.push(...pageResults);
    nextOffset += pageSize;
  }

  return allTags;
};

export const addTagToClient = async ({ clientId, tagId, loadingStatusOff = false, axiosConfig = {} }) => {
  validateClientId(clientId);
  const validatedTagId = validateTagId(tagId);
  return clientbookApi.post({
    url: `/clients/${clientId}/tags`,
    data: { tagId: validatedTagId },
    loading: !loadingStatusOff,
    axiosConfig,
  });
};

export const removeTagFromClient = async ({ clientId, tagId, loadingStatusOff = false, axiosConfig = {} }) => {
  validateClientId(clientId);
  const validatedTagId = validateTagId(tagId);
  return clientbookApi.delete({
    url: `/clients/${clientId}/tags/${validatedTagId}`,
    loading: !loadingStatusOff,
    axiosConfig,
  });
};

export const getClientRelationships = async ({ clientId, loadingStatusOff = false, axiosConfig = {} }) => {
  validateClientId(clientId);
  return clientbookApi.get({
    url: `/clients/${clientId}/relationships`,
    loading: !loadingStatusOff,
    axiosConfig,
  });
};

export const getRelationshipTypes = async ({ archived = false, loadingStatusOff = false, axiosConfig = {} } = {}) => {
  return clientbookApi.get({
    url: '/merchants/relationshiptypes',
    query: { archived },
    loading: !loadingStatusOff,
    axiosConfig,
  });
};

export const getClientPurchases = async ({ clientId, query = {}, loadingStatusOff = true, axiosConfig = {} }) => {
  const data = await clientbookApi.get({
    url: `/clients/${clientId}/sales`,
    query,
    loadingStatusOff,
    axiosConfig,
  });
  const dataFormatted = [];
  for (const transaction of data) {
    if (transaction?.product?.refNum) {
      let image_url = null;
      let sku = null;
      try {
        const productData = await clientbookApi.get({
          url: `/products/${transaction?.product?.refNum}`,
          query: {
            isPosRefNum: true,
          },
        });
        sku = productData?.sku;

        if (productData?.images?.length > 0) {
          const images = productData.images.filter(image => image.isPrimary);
          if (images.length > 0) {
            image_url = images[0].url;
          } else {
            image_url = productData.images[0].url;
          }
        }
      } catch (e) {
        image_url = null;
      }
      dataFormatted.push({
        ...transaction,
        product: {
          ...transaction.product,
          image_url,
          sku,
        },
      });
    } else {
      dataFormatted.push(transaction);
    }
  }
  return dataFormatted.map(transaction => ({
    productName: transaction.product?.name,
    name: transaction.product?.name,
    pos_ref_num: transaction.product?.refNum,
    storeName: transaction.store.name,
    transaction_date_time: transaction.transactionDateTime,
    transaction_type: transaction.transactionCodeTypeName,
    unit_price: transaction.unitPrice,
    unit_quantity: transaction.quantity,
    associate_ref: transaction?.associate?.refNum,
    product_pos_ref_num: transaction?.product?.refNum,
    transactionCode_type_disposition: transaction.transactionCodeTypeDisposition,
    sku: transaction?.product?.sku,
    image_url: transaction?.product?.image_url,
    description: transaction?.product?.name,
  }));
};

function parseData({
  name,
  email,
  mobile,
  preferredContactType,
  birthday,
  anniversary,
  storeId,
  associateId,
  doNotContact,
  preferredName,
  anonymous,
  marketing,
}) {
  const parsedData = {};
  if (name) parsedData.name = name;
  if (email || email === null) parsedData.email = email;
  if (mobile || mobile === null) parsedData.mobile = mobile;
  if (preferredContactType === 'SMS' || preferredContactType === 'EMAIL' || preferredContactType === 'WHATSAPP') {
    parsedData.preferredContactType = preferredContactType;
  }
  if (birthday) parsedData.birthday = birthday;
  if (anniversary) parsedData.anniversary = anniversary;
  if (storeId) parsedData.storeId = storeId;
  if (associateId !== null && associateId !== undefined) parsedData.associateId = associateId;
  if (doNotContact !== null && doNotContact !== undefined) parsedData.doNotContact = !!doNotContact;
  if (preferredName || preferredName === '' || preferredName === null) parsedData.preferredName = preferredName;
  if (anonymous !== null && anonymous !== undefined) parsedData.anonymous = !!anonymous;
  if (marketing !== null && marketing !== undefined) parsedData.marketing = !!marketing;
  return parsedData;
}

// Activity Attachment APIs

export const updateClientActivity = async ({ clientId, activityId, notes, loadingStatusOff = true }) => {
  return clientbookApi.patch({
    url: `/clients/${clientId}/activities/${activityId}`,
    data: { notes },
    loading: !loadingStatusOff,
  });
};

export const addActivityAttachments = async ({ clientId, activityId, attachments, loadingStatusOff = true }) => {
  return clientbookApi.post({
    url: `/clients/${clientId}/activities/${activityId}/attachments`,
    data: { attachments },
    loading: !loadingStatusOff,
  });
};

export const deleteActivityAttachment = async ({ clientId, activityId, attachmentId, loadingStatusOff = true }) => {
  return clientbookApi.delete({
    url: `/clients/${clientId}/activities/${activityId}/attachments/${attachmentId}`,
    loading: !loadingStatusOff,
  });
};
